import { CourseStatus } from '../enums';
import { OrderedItem } from '../interfaces';

export interface CourseItemGroup {
  trackId: number | string;
  coursePosition: number | null;
  courseName: string;
  items: Array<{ item: OrderedItem; index: number }>;
}

export interface KdsCourseTimelineGroup {
  trackId: number | string;
  coursePosition: number | null;
  courseName: string;
  isFired: boolean;
  isFlat: boolean;
  sendEventId?: number;
  dispatchedAt?: string;
  completedDuration?: number;
}

const NO_COURSE_TRACK_ID = 'no-course';

type KdsTimingItem = OrderedItem | { orderedItem?: OrderedItem; isReady?: boolean };

function getParentOrderedItemFromKdsItem(item: KdsTimingItem): OrderedItem {
  return (item as { orderedItem?: OrderedItem }).orderedItem || (item as OrderedItem);
}

function getItemDispatchTimeMs(
  item: OrderedItem,
  orderCreated?: Date | string
): number | null {
  const coursePosition =
    item.coursePosition !== null && item.coursePosition !== undefined
      ? Number(item.coursePosition)
      : null;
  const hasCourse = coursePosition !== null && !Number.isNaN(coursePosition);

  if (hasCourse) {
    if (item.courseFiredAt) {
      return new Date(item.courseFiredAt).getTime();
    }
    return null;
  }

  if (item.savedAt) {
    return new Date(item.savedAt).getTime();
  }

  if (orderCreated) {
    return new Date(orderCreated).getTime();
  }

  return null;
}

export function computeGroupTimestamps(
  items: OrderedItem[] = [],
  orderCreated?: Date | string
): Pick<KdsCourseTimelineGroup, 'dispatchedAt' | 'completedDuration'> {
  if (!items.length) {
    return {};
  }

  const dispatchTimes = items
    .map(item => getItemDispatchTimeMs(item, orderCreated))
    .filter((time): time is number => time !== null);

  if (!dispatchTimes.length) {
    return {};
  }

  const dispatchedAtMs = Math.min(...dispatchTimes);
  const dispatchedAt = new Date(dispatchedAtMs).toISOString();

  const allReady = items.every(item => item.isReady);
  if (!allReady) {
    return { dispatchedAt };
  }

  const readyTimes = items
    .map(item => (item.readyAt ? new Date(item.readyAt).getTime() : null))
    .filter((time): time is number => time !== null);

  if (readyTimes.length !== items.length) {
    return { dispatchedAt };
  }

  return {
    dispatchedAt,
    completedDuration: Math.max(0, Math.max(...readyTimes) - dispatchedAtMs),
  };
}

export function formatDurationMs(durationMs: number): string {
  const totalSeconds = Math.floor(durationMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number) => (value < 10 ? `0${value}` : `${value}`);

  if (hours) {
    return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  }

  return `${minutes}:${pad(seconds)}`;
}

export function getOldestUnfulfilledDispatchTime(
  items: KdsTimingItem[] = [],
  orderCreated?: Date | string
): string {
  const unfulfilledItems = items.filter(item => !item.isReady);

  if (!unfulfilledItems.length) {
    return orderCreated ? new Date(orderCreated).toISOString() : new Date().toISOString();
  }

  const dispatchTimes = unfulfilledItems
    .map(item => getItemDispatchTimeMs(getParentOrderedItemFromKdsItem(item), orderCreated))
    .filter((time): time is number => time !== null);

  if (!dispatchTimes.length) {
    return orderCreated ? new Date(orderCreated).toISOString() : new Date().toISOString();
  }

  return new Date(Math.min(...dispatchTimes)).toISOString();
}

export function groupItemsBySendEvent(items: OrderedItem[] = []): Map<number, OrderedItem[]> {
  const groups = new Map<number, OrderedItem[]>();

  items.forEach(item => {
    const sendEventId = item.sendEventId ?? 0;
    if (!groups.has(sendEventId)) {
      groups.set(sendEventId, []);
    }
    groups.get(sendEventId)!.push(item);
  });

  return groups;
}

function createSendEventTimelineGroups(
  items: OrderedItem[] = [],
  orderCreated?: Date | string
): KdsCourseTimelineGroup[] {
  const sendGroups = groupItemsBySendEvent(items);

  if (sendGroups.size <= 1) {
    return [];
  }

  return Array.from(sendGroups.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([sendEventId, groupItems], index) => ({
      trackId: `send-${sendEventId}`,
      coursePosition: null,
      courseName: `Send ${index + 1}`,
      isFired: true,
      isFlat: true,
      sendEventId,
      ...computeGroupTimestamps(groupItems, orderCreated),
    }));
}

export function groupOrderItemsByCourse(
  items: OrderedItem[] = [],
  seatFilter: number | null = null
): CourseItemGroup[] {
  const filtered = items.filter(item => {
    if (seatFilter === null) {
      return true;
    }
    const itemSeat =
      typeof item.seat === 'string' ? parseInt(item.seat as unknown as string, 10) : item.seat;
    return itemSeat === seatFilter;
  });

  const courseGroups = new Map<
    number,
    { courseName: string; items: Array<{ item: OrderedItem; index: number }> }
  >();
  const noCourseItems: Array<{ item: OrderedItem; index: number }> = [];

  filtered.forEach((item: OrderedItem, index: number) => {
    const coursePosition =
      item.coursePosition !== null && item.coursePosition !== undefined
        ? Number(item.coursePosition)
        : null;

    if (coursePosition !== null && !Number.isNaN(coursePosition)) {
      if (!courseGroups.has(coursePosition)) {
        courseGroups.set(coursePosition, {
          courseName: item.course || `Course ${coursePosition}`,
          items: [],
        });
      }
      courseGroups.get(coursePosition)!.items.push({ item, index });
    } else {
      noCourseItems.push({ item, index });
    }
  });

  const grouped: CourseItemGroup[] = Array.from(courseGroups.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([coursePosition, data]) => ({
      trackId: coursePosition,
      coursePosition,
      courseName: data.courseName,
      items: data.items,
    }));

  if (noCourseItems.length) {
    grouped.push({
      trackId: NO_COURSE_TRACK_ID,
      coursePosition: null,
      courseName: 'No Course',
      items: noCourseItems,
    });
  }

  return grouped;
}

export function orderHasCourseGroups(items: OrderedItem[] = []): boolean {
  return items.some(
    item => item.coursePosition !== null && item.coursePosition !== undefined
  );
}

export function isCourseGroupFired(group: CourseItemGroup): boolean {
  if (group.coursePosition === null) {
    return true;
  }
  return group.items.every(
    ({ item }) =>
      item.courseStatus === CourseStatus.FIRED || item.courseStatus === CourseStatus.COMPLETE
  );
}

export function canFireCourseGroup(
  group: CourseItemGroup,
  options: { orderId?: string; courseFireEnabled?: boolean } = {}
): boolean {
  const { orderId, courseFireEnabled = true } = options;
  if (!courseFireEnabled || !orderId || group.coursePosition === null) {
    return false;
  }
  return group.items.some(({ item }) => item.courseStatus === CourseStatus.PENDING);
}

export function getCourseFireLabel(group: CourseItemGroup, compact = false): string {
  if (group.coursePosition === null) {
    return '';
  }
  if (compact) {
    return 'Fire';
  }
  const name = group.courseName;
  const label = name.charAt(0) + name.slice(1).toLowerCase();
  return `Fire ${label}`;
}

export function isOrderedItemCourseFired(item: OrderedItem): boolean {
  const coursePosition =
    item.coursePosition !== null && item.coursePosition !== undefined
      ? Number(item.coursePosition)
      : null;

  if (coursePosition === null || Number.isNaN(coursePosition)) {
    return true;
  }

  return (
    item.courseStatus === CourseStatus.FIRED || item.courseStatus === CourseStatus.COMPLETE
  );
}

export function shouldShowOrderedItemOnKds(
  item: OrderedItem,
  _allItems: OrderedItem[] = []
): boolean {
  return true;
}

export function buildKdsCourseTimelineGroups(
  orderItems: OrderedItem[] = [],
  orderCreated?: Date | string
): KdsCourseTimelineGroup[] {
  const hasCourses = orderHasCourseGroups(orderItems);

  if (!hasCourses) {
    const sendEventGroups = createSendEventTimelineGroups(orderItems, orderCreated);
    if (sendEventGroups.length) {
      return sendEventGroups;
    }

    return [
      {
        trackId: NO_COURSE_TRACK_ID,
        coursePosition: null,
        courseName: '',
        isFired: true,
        isFlat: true,
        ...computeGroupTimestamps(orderItems, orderCreated),
      },
    ];
  }

  const allCourseGroups = groupOrderItemsByCourse(orderItems);
  const timelineGroups: KdsCourseTimelineGroup[] = allCourseGroups
    .filter(group => group.coursePosition !== null)
    .map(group => {
      const items = group.items.map(({ item }) => item);
      const isFired = isCourseGroupFired(group);

      return {
        trackId: group.trackId,
        coursePosition: group.coursePosition,
        courseName: group.courseName,
        isFired,
        isFlat: false,
        ...(isFired ? computeGroupTimestamps(items, orderCreated) : {}),
      };
    });

  const noCourseItems = orderItems.filter(item => {
    const coursePosition =
      item.coursePosition !== null && item.coursePosition !== undefined
        ? Number(item.coursePosition)
        : null;
    return coursePosition === null || Number.isNaN(coursePosition);
  });

  if (noCourseItems.length) {
    const sendEventGroups = createSendEventTimelineGroups(noCourseItems, orderCreated);
    if (sendEventGroups.length) {
      timelineGroups.push(...sendEventGroups);
    } else {
      timelineGroups.push({
        trackId: NO_COURSE_TRACK_ID,
        coursePosition: null,
        courseName: '',
        isFired: true,
        isFlat: true,
        ...computeGroupTimestamps(noCourseItems, orderCreated),
      });
    }
  }

  return timelineGroups;
}
